/**
 * A collapsible "Owned" drawer: the divider that already separated the
 * bought tiles from the ones on offer, made into its handle. Both shops
 * have one, so the behaviour lives here rather than twice.
 *
 * The label sits on the rule as a bordered chip, and opening lifts it up
 * and to the left with its shadow appearing beneath — the same gesture the
 * Settings / Rules / Stats disclosures use at the foot of the yard.
 *
 * Open or shut is a device preference rather than game progress, so it sits
 * in its own storage key alongside the mute and the chosen tab.
 */

const STORAGE_PREFIX = 'dinoclicker.drawer';

export interface Drawer {
  /** Append these to the panel, heading first. */
  readonly heading: HTMLButtonElement;
  readonly list: HTMLElement;
  /** Hides the drawer entirely, for when nothing is owned yet. */
  setPresent(present: boolean): void;
  /** How many tiles are inside; shown beside the label, and worth having
   * when the drawer is shut and the tiles cannot be counted by eye. */
  setCount(count: number): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** `name` keeps each drawer's state separate; two drawers need two names. */
export function createDrawer(label: string, name: string): Drawer {
  const key = `${STORAGE_PREFIX}.${name}`;

  // Default open, so a drawer that has never been touched shows what is in it.
  let open = true;
  try {
    open = window.localStorage.getItem(key) !== '0';
  } catch {
    // No storage; the drawer just won't remember.
  }

  const heading = el('button', 'upgrades__heading upgrades__drawer');
  heading.type = 'button';
  const chip = el('span', 'upgrades__drawer-chip');
  const text = el('span', 'upgrades__drawer-label', label);
  const count = el('span', 'upgrades__drawer-count');
  chip.append(text, count);
  heading.append(chip);

  const list = el('div', 'upgrades__list');

  const wrap = el('div', 'upgrades__drawer-wrap');
  wrap.hidden = true;
  wrap.append(heading, list);

  function apply(): void {
    list.hidden = !open;
    heading.setAttribute('aria-expanded', String(open));
    heading.setAttribute('aria-label', `${label}, ${open ? 'collapse' : 'expand'}`);
  }

  heading.addEventListener('click', () => {
    open = !open;
    apply();
    try {
      window.localStorage.setItem(key, open ? '1' : '0');
    } catch {
      // Preference just won't persist.
    }
  });
  apply();

  let lastCount = -1;
  return {
    heading,
    list,
    setPresent(present) {
      if (wrap.hidden === !present) return;
      wrap.hidden = !present;
    },
    setCount(n) {
      if (n === lastCount) return;
      lastCount = n;
      count.textContent = String(n);
    },
  };
}

/**
 * The element to append to the panel: the heading and its list together, so
 * hiding the drawer hides both.
 */
export function drawerElement(drawer: Drawer): HTMLElement {
  return drawer.heading.parentElement as HTMLElement;
}
